import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedProject } from "@/store/slices/projectSlice";
import { RootState } from "@/store/store";
import { IProject } from "@/interfaces/project.interface";

interface NotificationData {
  areaId: string;
  levelId: string;
  projectId: string;
}

function useNotificationHandler(
  setSelectedLevel: (levelId: string) => void,
  setShowAssignmentsDrawer: (open: boolean) => void,
  selectedAreaId: React.MutableRefObject<string | null>
) {
  const dispatch = useDispatch();
  const projects = useSelector((state: RootState) => state.projects.projects);
  const selectedProject = useSelector(
    (state: RootState) => state.projects.selectedProject
  );

  const pendingNotification = useRef<NotificationData | null>(null);

  // Handle notification click
  const handleNotificationClick = ({
    areaId,
    levelId,
    projectId,
  }: NotificationData) => {
    console.log("[Push] Handling notification:", {
      areaId,
      levelId,
      projectId,
    });
    if (projects.length === 0) {
      console.warn("[Push] Projects not loaded yet. Storing notification.");
      pendingNotification.current = { areaId, levelId, projectId };
      return;
    }

    if (selectedProject?.id !== projectId) {
      const project = projects.find((p) => p.id === projectId);
      if (!project) return;
      dispatch(setSelectedProject(project as IProject));
    }

    if (levelId !== selectedProject?.id) {
      setSelectedLevel(levelId);
    }

    selectedAreaId.current = areaId;
    setShowAssignmentsDrawer(true);
  };

  // Wait for projects to load before handling pending notification
  useEffect(() => {
    if (pendingNotification.current && projects.length > 0) {
      console.log("[Push] Projects are ready. Processing stored notification.");
      handleNotificationClick(pendingNotification.current);
      pendingNotification.current = null;
    }
  }, [projects]);

  // Listen for push notifications from the service worker
  useEffect(() => {
    if (!projects.length) return;

    if (!("serviceWorker" in navigator)) return;

    const listener = (event: MessageEvent) => {
      console.log("[Push] Notification Clicked Data:", event.data);
      if (event.data?.type === "OPEN_DETAILS") {
        handleNotificationClick(event.data.data);
      }
    };

    navigator.serviceWorker.addEventListener("message", listener);
    console.log("[Push] Added service worker listener for notification click.");

    return () => {
      console.log("[Push] Removing service worker listener.");
      navigator.serviceWorker.removeEventListener("message", listener);
    };
  }, [projects]);

  return { handleNotificationClick };
}

export default useNotificationHandler;
